/**
 * DNACore V4 — Lista de Avaliações (admin + Portal da Equipe)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, ClipboardCheck, BookOpen, Brain, RotateCcw, Sparkles, Mic, ListChecks, Trash2, Copy as CopyIcon, Edit3, Bookmark } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import './AssessmentEditor.css';

const ICONS = { FileText, ClipboardCheck, BookOpen, Brain, RotateCcw, Sparkles, Mic, ListChecks };
const TYPE_LABEL = {
  trimestral: 'Trimestral', apa: 'APA', trabalho: 'Trabalho', simulado: 'Simulado',
  recuperacao: 'Recuperação', diagnostica: 'Diagnóstica', chamada_oral: 'Chamada Oral', atividades: 'Atividades',
};
const STATUS_LABEL = { draft: 'Rascunho', review: 'Em revisão', approved: 'Aprovada', archived: 'Arquivada' };
const STATUS_COLOR = {
  draft: { bg: 'rgba(245,158,11,0.10)', fg: '#b45309', bd: 'rgba(245,158,11,0.22)' },
  review: { bg: 'rgba(6,182,212,0.10)', fg: '#0e7490', bd: 'rgba(6,182,212,0.22)' },
  approved: { bg: 'rgba(34,197,94,0.10)', fg: '#16a34a', bd: 'rgba(34,197,94,0.22)' },
  archived: { bg: 'rgba(15,23,42,0.06)', fg: '#475569', bd: 'rgba(15,23,42,0.08)' },
};

export default function AssessmentList({ basePath = '/v2/academico/editor-avaliacoes' }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [list, tpl, utpl] = await Promise.all([
        api.get('/academic/assessments'),
        api.get('/academic/assessments/templates'),
        api.get('/academic/assessments/user-templates').catch(() => ({ data: [] })),
      ]);
      setItems(list.data || []);
      setTemplates(tpl.data || []);
      setUserTemplates(utpl.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const createFromTemplate = async (tpl) => {
    try {
      const { data } = await api.post('/academic/assessments', {
        title: 'Nova ' + (tpl.label || TYPE_LABEL[tpl.key] || 'Avaliação'),
        type: tpl.key,
        content_html: tpl.content_html || '<p></p>',
        valor: tpl.default_valor ?? null,
        year: new Date().getFullYear(),
        template_key: tpl.key,
      });
      navigate(`${basePath}/${data.id}`);
    } catch (e) {
      toast.error('Falha ao criar: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Excluir "${it.title}"?`)) return;
    try {
      await api.delete(`/academic/assessments/${it.id}`);
      toast.success('Excluído');
      reload();
    } catch (e) {
      toast.error('Falha ao excluir: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDuplicate = async (it) => {
    try {
      await api.post(`/academic/assessments/${it.id}/duplicate`);
      toast.success('Duplicado');
      reload();
    } catch (e) {
      toast.error('Falha ao duplicar');
    }
  };

  const deleteUserTemplate = async (tpl) => {
    if (!window.confirm(`Excluir o modelo "${tpl.label}"?`)) return;
    try {
      await api.delete(`/academic/assessments/user-templates/${tpl.id}`);
      toast.success('Modelo removido');
      reload();
    } catch (e) {
      toast.error('Falha ao remover modelo');
    }
  };

  const createFromUserTemplate = async (utpl) => {
    try {
      const { data } = await api.post('/academic/assessments', {
        title: utpl.label || 'Nova Avaliação',
        type: utpl.type || 'trimestral',
        content_html: utpl.content_html || '<p></p>',
        valor: utpl.default_valor ?? null,
        year: new Date().getFullYear(),
        template_key: 'user_template',
      });
      navigate(`${basePath}/${data.id}`);
    } catch (e) {
      toast.error('Falha ao criar: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto' }} data-testid="assessment-list">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>Editor de Avaliações</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Crie, edite, imprima e exporte avaliações em formato A4.</p>
        </div>
      </div>

      {/* Modelos */}
      <div style={{ background: '#fff', borderRadius: 14, padding: 16, border: '1px solid rgba(15,23,42,0.06)', marginBottom: 24 }}>
        <h3 style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#475569', marginBottom: 10 }}>Começar com modelo</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          {templates.map((t) => {
            const Ico = ICONS[t.icon] || FileText;
            return (
              <button
                key={t.key}
                onClick={() => createFromTemplate(t)}
                data-testid={`ae-tpl-${t.key}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                  background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.10)',
                  borderRadius: 12, cursor: 'pointer', transition: 'all 140ms ease', textAlign: 'left',
                  color: '#0f172a',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.10)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(99,102,241,0.04)'; }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg, #6366f1, #8E52AB)', display: 'grid', placeItems: 'center', color: '#fff' }}>
                  <Ico size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>{t.label}</div>
                  <div style={{ fontSize: 10.5, color: '#64748b' }}>Modelo pronto</div>
                </div>
                <Plus size={14} style={{ color: '#6366f1' }} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Meus Modelos (biblioteca pessoal) */}
      {userTemplates.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 16, border: '1px solid rgba(245,158,11,0.18)', marginBottom: 24 }} data-testid="ae-user-templates">
          <h3 style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#b45309', marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Bookmark size={12} /> Meus Modelos
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {userTemplates.map((u) => (
              <div
                key={u.id}
                data-testid={`ae-user-tpl-${u.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                  background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.16)',
                  borderRadius: 12, transition: 'all 140ms ease', color: '#0f172a',
                }}
              >
                <button
                  onClick={() => createFromUserTemplate(u)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, background: 'transparent', border: 0, cursor: 'pointer', textAlign: 'left', padding: 0, color: 'inherit', minWidth: 0 }}
                  title="Criar avaliação a partir deste modelo"
                >
                  <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg, #f59e0b, #f97316)', display: 'grid', placeItems: 'center', color: '#fff', flexShrink: 0 }}>
                    <Bookmark size={14} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.label}</div>
                    <div style={{ fontSize: 10.5, color: '#92400e' }}>{u.description || 'Meu modelo pessoal'}</div>
                  </div>
                </button>
                <button
                  onClick={() => deleteUserTemplate(u)}
                  title="Remover modelo"
                  data-testid={`ae-user-tpl-del-${u.id}`}
                  style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626', border: 0, borderRadius: 7, padding: '6px 8px', cursor: 'pointer' }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista */}
      <h3 style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#475569', marginBottom: 10 }}>Suas avaliações</h3>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Carregando…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: 12, border: '1px solid rgba(15,23,42,0.06)' }}>
          Nenhuma avaliação criada. Escolha um modelo acima para começar.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {items.map((it) => {
            const sc = STATUS_COLOR[it.status] || STATUS_COLOR.draft;
            return (
              <div key={it.id} className="ae-list-card" data-testid={`ae-card-${it.id}`}>
                <div className="ph"><FileText size={20} /></div>
                <div className="info">
                  <div className="t">{it.title}</div>
                  <div className="s">
                    {TYPE_LABEL[it.type] || it.type}
                    {it.subject_name ? ` · ${it.subject_name}` : ''}
                    {it.class_name ? ` · ${it.class_name}` : ''}
                    {it.trimester ? ` · ${it.trimester}º trim ${it.year || ''}` : ''}
                  </div>
                </div>
                <span style={{
                  fontSize: 10.5, fontWeight: 800, padding: '3px 10px', borderRadius: 999,
                  background: sc.bg, color: sc.fg, border: `1px solid ${sc.bd}`,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>{STATUS_LABEL[it.status] || it.status}</span>
                <button onClick={() => navigate(`${basePath}/${it.id}`)} className="ae-list-act" title="Editar"
                  style={{ background: 'rgba(99,102,241,0.10)', color: '#4f46e5', border: 0, borderRadius: 9, padding: '8px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Edit3 size={13} /> Abrir
                </button>
                <button onClick={() => handleDuplicate(it)} title="Duplicar"
                  style={{ background: 'rgba(15,23,42,0.04)', color: '#475569', border: 0, borderRadius: 9, padding: '8px 10px', cursor: 'pointer' }}>
                  <CopyIcon size={13} />
                </button>
                <button onClick={() => handleDelete(it)} title="Excluir"
                  style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626', border: 0, borderRadius: 9, padding: '8px 10px', cursor: 'pointer' }}>
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
